import { Alert, Button, Skeleton, Typography } from 'antd';

import { formatCount, joinMeta } from '../../utils/counts';

const { Paragraph, Text } = Typography;

/**
 * What deleting an account takes, in words — from a delete preview's
 * `counts` (keepr-api `deletePreview`). ONE component for the self-serve
 * dialog (Profile) and the admin single and bulk dialogs (AdminUsers), so the
 * three can never describe the same cascade differently.
 *
 * The facts it states are the API's (keepr-api docs/SCHEMA.md "Deleting an
 * account"): the account and what is only its own go at once; the
 * collections it owns are removed with it and permanently deleted after
 * `purgeAfterDays`, and an administrator can restore them until then —
 * EXCEPT the personal collection, which is deleted for good (the API refuses
 * its revert, admins included: 409 personal_collection_locked). Every account
 * owns one, so the restore promise is only ever made for the others; the
 * first version made it for all of them, and for the common account whose
 * only collection is its personal one the dialog's one reassurance was false.
 * What the account put into OTHER people's collections — records and
 * attachments — stays. `itemsByOthers` is said out loud: until 2026-09 the
 * self-serve dialog never mentioned that a collaborator's records go with the
 * collection (delete audit F4).
 *
 *   owner       the sentence's subject — 'You' / 'This account' / 'These 3 accounts'
 *   plural      the subject takes a plural verb ('You own', 'These accounts own')
 *   counts      the preview counts; nothing renders without them
 *   collections the preview's `collections[]` rows, whose `kind` says which is
 *               personal. The bulk preview has no rows; its summed
 *               `counts.personalCollections` stands in
 *   loading     a skeleton in their place
 *   error       the preview request failed: said, with `onRetry` as a button,
 *               instead of a skeleton that never resolves
 */
export default function AccountDeleteSummary({ owner, plural = false, counts, collections = null, purgeAfterDays = 30, loading = false, error = false, onRetry = null }) {
	if (error) {
		return (
			<Alert
				type="error"
				showIcon
				className="mb-3"
				message="Could not load what this delete takes."
				action={onRetry ? <Button size="small" onClick={onRetry}>Retry</Button> : null}
			/>
		);
	}
	if (loading) return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
	if (!counts) return null;

	const self = owner === 'You';
	const own = plural || self ? 'own' : 'owns';
	const owns = joinMeta(
		formatCount(counts.collections, 'collection'),
		formatCount(counts.items, 'item'),
		formatCount(counts.attachments, 'attachment'),
	);
	const total = Number(counts.collections) || 0;
	const personal = Math.min(total, Array.isArray(collections)
		? collections.filter(c => c && c.kind === 'personal').length
		: Number(counts.personalCollections) || 0);
	const restorable = total - personal;
	const whose = self ? 'Your' : plural ? 'Their' : 'Its';
	const accounts = plural && !self ? 'accounts' : 'account';
	const others = Number(counts.itemsByOthers) || 0;
	const elsewhere = Number(counts.attachmentsElsewhere) || 0;

	return (
		<div className="account-delete-summary">
			<Paragraph>
				{owns ? (
					<>
						<Text strong>{owner} {own}:</Text> {owns}.
						{personal ? (
							<>
								{' '}{whose} {personal === 1 ? 'personal collection is' : `${personal} personal collections are`} deleted for good.
							</>
						) : null}
						{restorable ? (
							<>
								{' '}{personal
									? `The other ${restorable === 1 ? 'collection is' : `${restorable} collections are`}`
									: restorable === 1 ? 'It is' : 'They are'}{' '}
								deleted with the {accounts}; an administrator can restore {restorable === 1 ? 'it' : 'them'} for {purgeAfterDays} days, then {restorable === 1 ? 'it is' : 'they are'} erased for good.
							</>
						) : null}
					</>
				) : (
					<>{owner} {own} no collections.</>
				)}
			</Paragraph>
			{others ? (
				<Paragraph>
					<Text type="danger">
						{formatCount(others, 'item')} in {self ? 'your' : plural ? 'their' : 'its'} collections {others === 1 ? 'was' : 'were'} written by other people. {others === 1 ? 'It goes' : 'They go'} too.
					</Text>
				</Paragraph>
			) : null}
			<Paragraph type="secondary">
				Records {self ? 'you' : plural ? 'they' : 'it'} added to other people&apos;s collections stay with those collections
				{elsewhere ? `, and so ${elsewhere === 1 ? 'does' : 'do'} ${formatCount(elsewhere, 'attachment')} ${self ? 'you' : plural ? 'they' : 'it'} uploaded there` : ''}.
			</Paragraph>
		</div>
	);
}
